// Texas Hold'em with friends. Computer players fill empty seats while a person is sitting.
// The server deals, runs the clock and decides every hand; this page only shows it and sends your choice.
import { useEffect, useRef, useState } from 'react';
import { play, sequence } from '../fx/sound.ts';
import { fx } from '../fx/fx.tsx';
import { api } from '../api.ts';
import { useGame, money, fmt, ago } from '../state.tsx';
import { Loading, Panel, PlayerLink, go, useLoad } from '../components/ui.tsx';
import { Portrait } from '../art/Portrait.tsx';
import { CasinoNav, Card } from './Casino.tsx';

export function PokerPage({ table }: { table?: string }) {
  return (
    <div>
      <CasinoNav game="poker" />
      {table ? <PokerTable tableId={table} /> : <Lobby />}
    </div>
  );
}

function Lobby() {
  const { me, act } = useGame();
  const { data, reload } = useLoad(() => api.get('/api/poker'), [], 5000);
  const [buy, setBuy] = useState<Record<string, string>>({});
  if (!data) return <Loading />;
  const sit = async (t: any) => {
    const amount = Math.floor(Number((buy[t.id] ?? String(t.minBuy * 5 > t.maxBuy ? t.maxBuy : t.minBuy * 5)).replace(/[^0-9]/g, '')));
    const r = await act('/api/poker/sit', { tableId: t.id, buyIn: amount });
    if (r) go(`poker/${t.id}`);
    else reload();
  };
  return (
    <div>
      <p className="muted">
        No-limit Texas Hold’em against your friends. Buy in with cash from your pocket; when you stand up, your chips go back into your pocket.
        Computer players fill empty seats while at least one person is playing, so there’s always a game.
      </p>
      {data.mine && <div className="panel"><div className="panel-body">You’re sitting at a table. <a onClick={() => go(`poker/${data.mine}`)}>Go back to your seat →</a></div></div>}
      <div className="poker-lobby">
        {data.tables.map((t: any) => {
          const locked = me.level < t.minLevel;
          const def = t.minBuy * 5 > t.maxBuy ? t.maxBuy : t.minBuy * 5;
          return (
            <div key={t.id} className={`panel poker-lobby-table ${locked ? 'locked' : ''}`}>
              <div className="panel-title">{t.name}</div>
              <div className="panel-body">
                <div>Blinds <b>{money(t.small)} / {money(t.big)}</b></div>
                <div className="small muted">Buy-in {money(t.minBuy)} – {money(t.maxBuy)}{t.minLevel > 1 && <> · level {t.minLevel}+</>}</div>
                <div className="small" style={{ margin: '4px 0' }}>
                  {t.players.length === 0 ? <span className="dim">Empty table</span> : t.players.map((p: any, i: number) => <span key={i} className={p.isAi ? 'dim' : 'gold'}>{i ? ', ' : ''}{p.name}</span>)}
                </div>
                {data.mine === t.id ? (
                  <button className="btn green" onClick={() => go(`poker/${t.id}`)}>Return to table</button>
                ) : locked ? <div className="small"><span className="locked-stamp">Locked</span> until level {t.minLevel}</div> : data.mine ? null : (
                  <div className="row" style={{ gap: 4 }}>
                    <input value={buy[t.id] ?? String(def)} onChange={(e) => setBuy({ ...buy, [t.id]: e.target.value })} style={{ width: 90 }} aria-label="buy-in" />
                    <button className="btn green" onClick={() => sit(t)}>Sit down</button>
                    <a className="small" onClick={() => go(`poker/${t.id}`)} style={{ alignSelf: 'center' }}>watch</a>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="small dim">Your record: {fmt(data.me.hands)} pots won · net {data.me.net >= 0 ? '+' : ''}{money(data.me.net)}</div>
      {data.recentBig.length > 0 && (
        <Panel title="Big pots">
          {data.recentBig.map((e: any, i: number) => <div key={i} className="feed-item"><span className="grow">{e.text.replace(/[{}]/g, '')}</span><span className="when">{ago(e.createdAt)}</span></div>)}
        </Panel>
      )}
    </div>
  );
}

const STAGE: Record<string, string> = { waiting: 'Waiting for players', preflop: 'Pre-flop', flop: 'The flop', turn: 'The turn', river: 'The river', showdown: 'Showdown' };

/** Where your own seat is on screen (bottom of the table), for the floating winnings. */
function centerOfSeat() {
  const el = document.querySelector('.poker-seat.p0');
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { x: r.left + r.width / 2, y: r.top };
}

function PokerTable({ tableId }: { tableId: string }) {
  const { me, act, content } = useGame();
  const { data, reload } = useLoad(() => api.get(`/api/poker/${tableId}`), [tableId], 1200);
  const [raise, setRaise] = useState(0);
  const [buyIn, setBuyIn] = useState('');
  const [busy, setBusy] = useState(false);
  const [, tick] = useState(0);
  useEffect(() => {
    const h = setInterval(() => tick((x) => x + 1), 500);
    return () => clearInterval(h);
  }, []);
  const a = data?.actions;
  useEffect(() => {
    if (a) setRaise(a.minRaiseTo);
  }, [data?.handNo, data?.stage, !!a]);
  // Sounds: compare each refresh of the table with the one before.
  const prev = useRef<any>(null);
  useEffect(() => {
    const p = prev.current;
    prev.current = data;
    if (!data || !p || p.table.id !== data.table.id) return;
    const steps: [number, string][] = [];
    if (data.handNo !== p.handNo && data.handNo > 0) {
      const n = data.seats.filter((x: any) => x?.inHand).length;
      for (let i = 0; i < Math.min(n * 2, 8); i++) steps.push([i * 90, 'deal']);
    } else {
      for (let i = p.board.length; i < data.board.length; i++) steps.push([(i - p.board.length) * 160, 'deal']);
      data.seats.forEach((x: any, i: number) => {
        const before = p.seats[i];
        if (!x || !before || x.lastAction === before.lastAction || !x.lastAction) return;
        const act = String(x.lastAction).toLowerCase();
        if (act.includes('fold')) steps.push([0, 'flip']);
        else if (act.includes('check')) steps.push([0, 'knock']);
        else steps.push([0, 'chips']);
      });
    }
    if (data.showdown && !p.showdown) {
      steps.push([300, 'chipSlide']);
      const mine = data.showdown.winners.find((w: any) => w.seat === data.mySeat);
      if (mine) {
        steps.push([800, 'win']);
        setTimeout(() => fx.float(`+${money(mine.amount)}`, 'cash', centerOfSeat()), 800);
      }
    }
    if (data.mySeat >= 0 && data.toAct === data.mySeat && p.toAct !== data.mySeat && data.actions) steps.push([200, 'knock']);
    // Same sound at the same moment only once.
    const seen = new Set<string>();
    sequence(steps.filter(([ms, n]) => { const k = `${ms}${n}`; if (seen.has(k)) return false; seen.add(k); return true; }));
  }, [data]);
  if (!data) return <Loading />;
  const t = data.table;
  const offset = data.serverNow - Date.now();
  const left = data.deadline ? Math.max(0, Math.ceil((data.deadline - (Date.now() + offset)) / 1000)) : 0;
  const seated = data.mySeat >= 0;
  const send = async (action: string, raiseTo = 0) => {
    if (busy) return;
    setBusy(true);
    play(action === 'fold' ? 'flip' : action === 'check' ? 'knock' : 'chips');
    await act('/api/poker/act', { tableId, action, raiseTo });
    setBusy(false);
    reload();
  };
  const sit = async () => {
    const amount = Math.floor(Number((buyIn || String(Math.min(t.maxBuy, t.minBuy * 5))).replace(/[^0-9]/g, '')));
    if (await act('/api/poker/sit', { tableId, buyIn: amount })) play('chips');
    reload();
  };
  const stand = async () => {
    const r = await act('/api/poker/stand', { tableId }, { success: (x) => ({ kind: 'good', text: <>You stand up and pocket <span className="money">{money(x.chips)}</span>.</> }) });
    if (r) go('poker');
  };
  const winners = new Map((data.showdown?.winners ?? []).map((w: any) => [w.seat, w]));
  // Put "me" at the bottom of the oval.
  const rot = seated ? data.mySeat : 0;
  const pos = (i: number) => (i - rot + 6) % 6;

  return (
    <div>
      <div className="row" style={{ alignItems: 'center', marginBottom: 6 }}>
        <a onClick={() => go('poker')}>← All tables</a>
        <span className="grow" style={{ marginLeft: 10 }}><b>{t.name}</b> · blinds {money(t.small)}/{money(t.big)}</span>
        {seated && <button className="btn" onClick={stand}>Stand up</button>}
      </div>
      <div className="poker-felt">
        <div className="poker-center">
          <div className="small gold">{STAGE[data.stage] ?? data.stage}{data.handNo > 0 && ` · hand #${data.handNo}`}</div>
          <div className="cards-row poker-board">
            {data.board.map((c: string, i: number) => <Card key={`${data.handNo}-${i}`} code={c} />)}
            {Array.from({ length: 5 - data.board.length }).map((_, i) => <div key={`e${i}`} className="card-slot" />)}
          </div>
          <div className="poker-pot">Pot: <span className="money">{money(data.pot)}</span></div>
          {data.stage === 'waiting' && <div className="small muted">{data.nextHandAt ? 'Next hand starting…' : 'Waiting for players to sit down.'}</div>}
        </div>
        {data.seats.map((s: any, i: number) => (
          <div key={i} className={`poker-seat p${pos(i)} ${s && data.toAct === i ? 'acting' : ''} ${s?.folded && s.inHand ? 'folded' : ''} ${winners.has(i) ? 'winner' : ''}`}>
            {!s ? <div className="empty-seat">empty seat</div> : (
              <>
                <div className="seat-top">
                  <Portrait seed={s.portrait} playerId={s.isAi ? undefined : s.playerId} size={34} />
                  <div className="seat-info">
                    <div className="seat-name">{s.isAi ? s.name : <PlayerLink id={s.playerId} name={s.name} />}{data.button === i && <span className="dealer-btn" title="Dealer">D</span>}</div>
                    <div className="money small">{money(s.chips)}</div>
                  </div>
                </div>
                <div className="seat-cards">
                  {s.cards ? s.cards.map((c: string, k: number) => <Card key={`${data.handNo}-${k}`} code={c} delay={k * 90} />) : s.inHand && !s.folded ? <><div key={`${data.handNo}a`} className="card-back deal-in" /><div key={`${data.handNo}b`} className="card-back deal-in" style={{ animationDelay: '90ms' }} /></> : null}
                </div>
                <div className="seat-status small">
                  {winners.has(i) && <span key={data.handNo} className="pot-chips" aria-hidden="true"><i /><i /><i /></span>}
                  {winners.has(i) ? <b className="gold">Wins {money((winners.get(i) as any).amount)}</b> : s.allIn ? <b className="bad">ALL IN</b> : s.lastAction ? s.lastAction : ''}
                  {s.bet > 0 && <span className="seat-bet"> {money(s.bet)}</span>}
                  {s.hand && <div className="dim">{s.hand}</div>}
                  {data.toAct === i && left > 0 && <div className="seat-timer">{left}s</div>}
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {seated && a ? (
        <div className="poker-actions panel">
          <div className="panel-body">
            <div className="small gold" style={{ marginBottom: 4 }}>Your turn · {left}s left</div>
            <div className="row" style={{ gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <button className="btn red" onClick={() => send('fold')}>Fold</button>
              {a.canCheck ? <button className="btn" onClick={() => send('check')}>Check</button> : <button className="btn green" onClick={() => send('call')}>Call {money(a.toCall)}</button>}
              {a.maxRaiseTo > a.minRaiseTo && (
                <>
                  <input type="range" min={a.minRaiseTo} max={a.maxRaiseTo} step={a.bigBlind} value={raise} onChange={(e) => setRaise(Number(e.target.value))} className="raise-slider" aria-label="raise amount" />
                  <button className="btn blue" onClick={() => send('raise', raise)}>{a.canCheck ? 'Bet' : 'Raise to'} {money(raise)}</button>
                </>
              )}
              <button className="btn" onClick={() => send('allin')}>All in ({money(a.maxRaiseTo)})</button>
            </div>
          </div>
        </div>
      ) : seated ? (
        <div className="small muted center" style={{ margin: 6 }}>{data.stage === 'waiting' ? 'Waiting for the next hand…' : 'Waiting for the others…'} If you don’t act in {content.rules.poker.turnSeconds} seconds you check or fold automatically.</div>
      ) : (
        <div className="panel"><div className="panel-body row" style={{ gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <span className="grow">You’re watching. Buy in for {money(t.minBuy)} – {money(t.maxBuy)}:</span>
          <input value={buyIn} placeholder={String(Math.min(t.maxBuy, t.minBuy * 5))} onChange={(e) => setBuyIn(e.target.value)} style={{ width: 100 }} aria-label="buy-in" />
          <button className={`btn green ${me.level < t.minLevel ? 'disabled' : ''}`} onClick={sit}>Sit down</button>
        </div></div>
      )}

      <Panel title="Table talk">
        <div className="poker-log">
          {data.log.slice(-14).reverse().map((l: string, i: number) => <div key={i} className={l.startsWith('—') ? 'gold' : ''}>{l}</div>)}
        </div>
      </Panel>
    </div>
  );
}
